/**
 * @sazeo/sdk — AI / BYOK hooks
 *
 * Lets a tenant administrator configure their own Anthropic key (BYOK).
 * Reads key presence from `ai_quotas.byok_anthropic_key_hash` (non-null = configured).
 * Write/delete go through RPC so the plaintext key never touches the client DB layer.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ByokKeyStatus {
  configured: boolean;
  /** SHA-256 fingerprint prefix shown in the UI, e.g. "a1b2c3d4…" */
  fingerprint: string | null;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Reads `ai_quotas` to check whether a BYOK Anthropic key is configured.
 * Returns `{ configured: true, fingerprint: "…" }` when
 * `byok_anthropic_key_hash` is non-null.
 */
export function useByokKeyStatus(
  supabase: SupabaseClientLike,
  tenantId: string,
): ReturnType<typeof useQuery<ByokKeyStatus>> {
  return useQuery<ByokKeyStatus>({
    queryKey: ['ai', 'byok', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_quotas')
        .select('byok_anthropic_key_hash')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (error) throw error;

      const hash: string | null =
        (data as { byok_anthropic_key_hash: string | null } | null)?.byok_anthropic_key_hash ??
        null;

      return {
        configured: hash !== null,
        fingerprint: hash ? `${hash.slice(0, 8)}…` : null,
      };
    },
  });
}

/**
 * Calls `set_tenant_byok_key(p_tenant_id, p_key)` RPC.
 * On success, invalidates the byok status query.
 */
export function useSetByokKey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (key: string) => {
      if (!supabase.rpc) throw new Error('rpc not available');
      const { error } = await supabase.rpc('set_tenant_byok_key', {
        p_tenant_id: tenantId,
        p_key: key,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai', 'byok', tenantId] });
    },
  });
}

/**
 * Calls `delete_tenant_byok_key(p_tenant_id)` RPC.
 * On success, invalidates the byok status query.
 */
export function useDeleteByokKey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!supabase.rpc) throw new Error('rpc not available');
      const { error } = await supabase.rpc('delete_tenant_byok_key', {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai', 'byok', tenantId] });
    },
  });
}
